import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmt, fmtDateShort } from '../lib/format';
import { Users, ArrowLeft } from '@phosphor-icons/react';

export function CustomerList() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    api.get('/customers').then(r => { setCustomers(r.data); setLoading(false); });
  }, []);

  const filtered = customers.filter(c => !query || c.email.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-5 fade-in" data-testid="customers-page">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">客户管理</h1>
        <p className="text-sm text-slate-400 mt-0.5">所有客户及其累计数据</p>
      </div>

      <input type="text" placeholder="搜索客户邮箱…" className="input-base max-w-sm"
             value={query} onChange={e => setQuery(e.target.value)} data-testid="customers-search" />

      {loading && <div className="card-base p-12 text-center text-slate-500">加载中…</div>}

      {!loading && filtered.length === 0 && (
        <div className="card-base p-12 text-center">
          <Users size={40} weight="duotone" className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">还没有客户</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1F2B45] text-xs uppercase text-slate-400 font-semibold tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">客户 Email</th>
                  <th className="px-4 py-3 text-right">笔数</th>
                  <th className="px-4 py-3 text-right">累计 MYR</th>
                  <th className="px-4 py-3 text-right">累计 USDT</th>
                  <th className="px-4 py-3 text-right">Gross USDT</th>
                  {isAdmin && <th className="px-4 py-3 text-right">利润</th>}
                  <th className="px-4 py-3 text-right">最近</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.email} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                    <td className="px-4 py-3 text-sm">
                      <Link to={`/customers/${encodeURIComponent(c.email)}`}
                            className="text-indigo-300 hover:text-indigo-200">{c.email}</Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-num text-slate-300">{c.order_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-right font-num text-amber-300">{fmt(c.total_amount_myr)}</td>
                    <td className="px-4 py-3 text-sm text-right font-num text-emerald-300">{fmt(c.total_amount_usdt)}</td>
                    <td className="px-4 py-3 text-sm text-right font-num text-white">{fmt(c.total_gross_usdt)}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm text-right font-num text-emerald-400 font-semibold">
                        {fmt(c.total_profit_usdt)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs text-right text-slate-500 font-num">{fmtDateShort(c.last_order_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function CustomerDetail() {
  const { email } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    api.get(`/customers/${encodeURIComponent(email)}`).then(r => { setData(r.data); setLoading(false); });
  }, [email]);

  if (loading) return <div className="card-base p-12 text-center text-slate-500">加载中…</div>;
  if (!data) return <div className="text-slate-500">客户不存在</div>;

  const { customer, orders } = data;

  return (
    <div className="space-y-5 fade-in max-w-4xl">
      <Link to="/customers" className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5">
        <ArrowLeft size={14} /> 返回客户列表
      </Link>

      <div className="card-base p-5 sm:p-6">
        <div className="mb-4">
          <h1 className="text-xl font-semibold text-white break-all">{customer.email}</h1>
          <p className="text-xs text-slate-500 mt-1 font-num">
            首次:{fmtDateShort(customer.created_at)} · 最近:{fmtDateShort(customer.last_order_at)}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <Stat label="累计笔数" value={customer.order_count || 0} />
          <Stat label="累计 MYR" value={fmt(customer.total_amount_myr)} accent="amber" />
          <Stat label="累计 USDT" value={fmt(customer.total_amount_usdt)} accent="emerald" />
          <Stat label="Gross USDT" value={fmt(customer.total_gross_usdt)} accent="indigo" />
          {isAdmin && (
            <>
              <Stat label="汇率利润" value={fmt(customer.total_exchange_profit_usdt)} accent="emerald" />
              <Stat label="手续费利润" value={fmt(customer.total_fee_usdt)} accent="emerald" />
              <Stat label="总利润" value={fmt(customer.total_profit_usdt)} accent="emerald" big />
              <Stat label="最近卖出汇率" value={customer.last_sell_rate || '-'} />
            </>
          )}
        </div>
      </div>

      <div className="card-base p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">订单历史 ({orders.length})</h2>
        <div className="space-y-2">
          {orders.map(o => (
            <div key={o.id} className="p-3 bg-slate-900/40 rounded-lg border border-slate-800 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs text-slate-500 font-num">{fmtDateShort(o.created_at)} · {o.employee_name}</div>
                <div className="font-num text-sm text-white mt-0.5">
                  {fmt(o.amount)} {o.currency} {o.sell_rate ? `@ ${o.sell_rate}` : ''} · 费 {o.fee_percent}%
                </div>
              </div>
              <div className="text-right font-num">
                <div className="text-sm text-emerald-300">Net {fmt(o.net_usdt)} <span className="text-xs text-slate-500">USDT</span></div>
                {isAdmin && <div className="text-xs text-emerald-400 mt-0.5">+{fmt(o.total_profit_usdt)} 利润</div>}
              </div>
            </div>
          ))}
          {orders.length === 0 && <div className="text-sm text-slate-500 text-center py-4">没有订单记录</div>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, big }) {
  const cls = {
    amber: 'text-amber-300', emerald: 'text-emerald-300', indigo: 'text-indigo-300',
  }[accent] || 'text-white';
  return (
    <div>
      <div className="label-tiny mb-1">{label}</div>
      <div className={`font-num font-semibold ${cls} ${big ? 'text-2xl' : 'text-lg'}`}>{value}</div>
    </div>
  );
}
